import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { newCommandId, type CommandReply, type RefusalError } from "../../api/client";
import { queryKey, runQuery, runUpload, type CommandInput } from "../../api/endpoints";
import { useApiQuery } from "../../api/hooks";
import type { CommandName, CommandResponses, QueryName } from "../../api/routes";
import type { DeliverableKind, ElementKind, ElementRow, ElementSummary, Id, Json } from "../../api/types";

/** A value that follows `value` after it has stood still for `ms`. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [shown, setShown] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setShown(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return shown;
}

/**
 * Make the queries of some endpoints stale after a command. The live channel does the same for everyone from the
 * event notice; doing it here as well keeps this person's screen right when the channel is slow or closed.
 */
export function useRefresh(): (...names: QueryName[]) => void {
  const client = useQueryClient();
  return useCallback(
    (...names: QueryName[]) => {
      for (const name of names) void client.invalidateQueries({ queryKey: [name] });
    },
    [client],
  );
}

/**
 * One command id for a form, kept until the step goes through. Pressing the button again after a lost connection
 * sends the same id, so a repeat changes nothing (2.1.4). `renew` starts the next step.
 */
export function useCommandId(): { current: () => string; renew: () => void } {
  const ref = useRef<string | null>(null);
  const current = useCallback(() => {
    if (!ref.current) ref.current = newCommandId();
    return ref.current;
  }, []);
  const renew = useCallback(() => {
    ref.current = null;
  }, []);
  return { current, renew };
}

export function isGoneState(state: string | null | undefined): boolean {
  return state === "withdrawn" || state === "removed" || state === "rejected";
}

/** The elements of one kind that are still in use, for a list to choose from. */
export function useElementList(kind: ElementKind | null, options: { q?: string; limit?: number; enabled?: boolean } = {}) {
  const query = useApiQuery(
    "list_elements",
    { kind, q: options.q || undefined, limit: options.limit ?? 100, offset: 0 },
    { enabled: (options.enabled ?? true) && kind !== null, staleTime: 15_000 },
  );
  const items: ElementSummary[] = (query.data?.items ?? []).filter((item) => !isGoneState(item.state));
  // `read` is how many the server sent, before the ones no longer in use were left out; it tells whether the list is cut short.
  return { ...query, items, total: query.data?.total ?? 0, read: query.data?.items.length ?? 0 };
}

export interface DocumentKindInfo {
  id: Id;
  code: string;
  name: string;
  origin: ElementSummary["origin"];
  /** Known once the element has been read. */
  deliverableKind: DeliverableKind | null;
  isDrawing: boolean;
  row: ElementRow | null;
}

function contentOf(row: ElementRow | undefined): Record<string, Json> {
  return row?.content ?? {};
}

/**
 * The kinds of document of the discipline, each with its kind of deliverable and whether it is a drawing. The list
 * query gives names only, so each kind is read once (the reads are cached and shared with the element page).
 */
export function useDocumentKinds(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled ?? true;
  const list = useElementList("document_kind", { limit: 200, enabled });
  const reads = useQueries({
    queries: list.items.map((item) => ({
      queryKey: queryKey("element", { id: item.id }) as unknown as readonly unknown[],
      queryFn: () => runQuery("element", { id: item.id }),
      staleTime: 30_000,
      enabled,
    })),
  });
  const kinds: DocumentKindInfo[] = list.items.map((item, index) => {
    const row = reads[index]?.data?.element;
    const content = contentOf(row);
    const letter = content.deliverable_kind;
    return {
      id: item.id,
      code: item.code,
      name: item.name,
      origin: item.origin,
      deliverableKind: letter === "A" || letter === "B" || letter === "C" ? letter : null,
      isDrawing: content.is_drawing === true,
      row: row ?? null,
    };
  });
  const loading = list.isPending || reads.some((read) => read.isPending);
  return { kinds, drawings: kinds.filter((kind) => kind.isDrawing), loading, error: list.error };
}

/** The name of an element for a line of text, read once. */
export function useElementName(id: Id | null | undefined): string | null {
  const query = useApiQuery("element", { id: id ?? "" }, { enabled: !!id, staleTime: 30_000 });
  return query.data?.element.name ?? null;
}

type UploadInput<K extends CommandName> = CommandInput<K> & { command_id?: string };

/** Send a command that carries a file (a document, a symbol, a list of codes): multipart with a `command_id` form field. */
export function useApiUpload<K extends CommandName>(name: K) {
  return useMutation<CommandReply<CommandResponses[K]>, RefusalError, { input: UploadInput<K>; file: File | Blob }>({
    mutationFn: ({ input, file }) => runUpload(name, input as CommandInput<K>, file),
  });
}
